import { Injectable } from '@angular/core';
import { AbstractDataProvider } from '@epicuro-next/data-providers/base-data-provider';
import {
  DocumentControlRuleType,
  selectIsDocumentControlEnabled,
  selectListValidatorValue,
  State as DocumentControlState,
} from '@epicuro-next/state/document-control-state';
import {
  selectOpenedListFromListEntity,
  selectOpenedListId,
} from '@epicuro-next/state/list-entities';
import { State as ProductListState } from '@epicuro-next/state/product-list-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, shareReplay, switchMap, takeUntil } from 'rxjs/operators';

type AppState = DocumentControlState & ProductListState;

@Injectable()
export class CommercialListProviderService implements AbstractDataProvider {
  private bindings = new Map<string, Observable<unknown>>();
  private destroy$ = new Subject<void>();

  private list$ = this.store.pipe(
    select(selectOpenedListFromListEntity),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );

  private listId$ = this.store.pipe(
    select(selectOpenedListId),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  private isDocumentControlEnabled$ = this.store.pipe(
    select(selectIsDocumentControlEnabled),
  );

  private adjustNumberOfItems$ = this.listId$.pipe(
    filter((id) => isNonNullish(id)),
    switchMap((id) =>
      this.store.pipe(
        select(
          selectListValidatorValue(
            id as string,
            DocumentControlRuleType.AdjustNumberOfItems,
          ),
        ),
      ),
    ),
  );

  constructor(private store: Store<AppState>) {
    this.initBindings();
  }

  public read<T>(property: string): Observable<T> {
    const observable = this.bindings.get(property);

    if (!observable) {
      throw new Error('Undefined property of ListAccessProviderService');
    }

    return observable as Observable<T>;
  }

  public write() {
    // here for eslint
  }

  private initBindings() {
    const bindingsEntries: [string, Observable<unknown>][] = [
      ['list', this.list$],
      ['isDocumentControlEnabled', this.isDocumentControlEnabled$],
      ['adjustNumberOfItems', this.adjustNumberOfItems$],
    ];

    bindingsEntries.forEach(([propertyName, observer]) => {
      this.initBinding(propertyName, observer);
    });
  }

  private initBinding(propertyName: string, observer: Observable<unknown>) {
    this.bindings.set(
      propertyName,
      observer.pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        takeUntil(this.destroy$),
      ),
    );
  }
}
